/**
 * 設定 → 送付状（#223、docs/89 §4。iOS のまとまり＝docs/10 §9.15）。書くのは管理者、閲覧は全員（押せない形）。
 *   SettingsCoverLettersPage（/settings/cover-letters）：節「テンプレート」（行・既定の印・「テンプレート追加」）／節「既定」（印刷で送付状＝押した瞬間に保存）／
 *     節「窓付封筒」（名前の横の「？」：送付状の宛名を封筒の窓に合わせます）の「窓付封筒 ›」＝下の画面
 *   SettingsCoverEnvelopePage（/settings/cover-letters/envelope、2026-10-21 の改め＝docs/89 §4.1）：上に封筒の図（長3 を宛名を読む向き＝横長・窓・紙＝三つ折りの上の段・
 *     宛名の枠と見本の宛名。寸法の数字を辺に）、下に 節「窓」左端から・上端から・幅・高さ／節「宛名の微調整」上下・左右（mm・0.5mm 刻み・自動で保存）・
 *     位置合わせシート ›・既定に戻す。打つとその場で図が動き、収まらなければ図の宛名を朱・欄の下に朱の1行。宛名の枠の計算は packages/paper の coverAddressLayout（見本・出力の PDF と同じ）
 *   SettingsCoverLetterPage（/settings/cover-letters/:id）：テンプレート名／節「FAX」題・挨拶／節「窓付封筒」題・挨拶／節「本文」本文・備考／
 *     節「印字項目」5つのスイッチ／見本（FAX｜窓付封筒 の切替で紙の見本）／既定に設定・削除（既定は削除できない）。
 *     打つ欄は自動で保存（lib/autoSave.tsx。欄を離れたとき）。版つき（#202：ぶつかったら最新を読んで直した欄だけ重ねて送り直す）
 *   見本は API の PDF（GET /cover-letters/templates/:id/sample）＝出力の PDF と同じ描き手。金額は出ない（★4）
 */
import {
  COVER_ADDRESS_HEADER,
  COVER_ENVELOPE_DEFAULT,
  COVER_ENVELOPE_KINDS,
  COVER_ENVELOPE_RANGE,
  COVER_GREETING_LABEL,
  COVER_LETTER_LIMITS,
  COVER_LETTER_MSG as M,
  COVER_PAPER_IN_ENVELOPE,
  CoverLetterTemplateInput,
  SETTINGS_MSG,
  coverEnvelopeError,
  type CoverEnvelope,
  type CoverGreeting,
  type CoverLetterSettings,
  type CoverLetterTemplate,
  type CoverMode,
} from "@genbaryoku/contract";
import { coverAddressLayout, coverSampleRecipient, type CoverAddressLayout } from "@genbaryoku/paper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, fetchFile, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupInputRow, GroupList, GroupNavRow, GroupNumberRow, GroupSection, GroupSwitchRow, GroupTextRow, GroupValueRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { AutoSaveNote, AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";
import { usePdfTab } from "../lib/pdfOpen.js";
import { renderPdf, type PageImg } from "../lib/pdfPages.js";
import { useSettingsPane } from "../lib/settingsPane.js";

export const COVER_SETTINGS_KEY = ["cover-letter-settings"] as const;
const KEY = COVER_SETTINGS_KEY;

export function SettingsCoverLettersPage() {
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const nav = useNavigate();
  const pane = useSettingsPane();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<CoverLetterSettings>("GET", "/cover-letters/settings") });
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(M.settingsTitle);
  const savePrint = useMutation({
    mutationFn: (v: boolean) => api<CoverLetterSettings>("PUT", "/cover-letters/settings", { printDefault: v }),
    onSuccess: (r) => {
      qc.setQueryData(KEY, r);
      toast(`${M.printDefault}を${r.printDefault ? "オン" : "オフ"}にしました。`);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const add = useMutation({
    mutationFn: () => api<CoverLetterTemplate>("POST", "/cover-letters/templates", {}),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: KEY });
      nav(`/settings/cover-letters/${t.id}`);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const printOn = savePrint.isPending && savePrint.variables !== undefined ? savePrint.variables : q.data.printDefault;
  return (
    <div className="ppage estpage" data-testid="cover-settings">
      {pane.twoCol ? null : (
        <div className="cotop">
          <BackButton back={back} />
        </div>
      )}
      {back.dialog}
      <div className="ptitle-row">
        <h1 className="ptitle">{M.settingsTitle}</h1>
        <Help text={M.settingsHelp} testId="help-cover-settings" />
      </div>
      {!admin ? <p className="est-note" data-testid="cover-member-note">{SETTINGS_MSG.memberNote}</p> : null}

      <GroupSection title={M.sectionTemplates} data-testid="cover-sec-templates">
        <GroupList>
          {q.data.templates.map((t) => (
            <GroupNavRow key={t.id} className="cl-trow" title={t.name} value={t.isDefault ? M.defaultMark : ""} to={`/settings/cover-letters/${t.id}`} testId="cover-template-row" />
          ))}
          {admin ? <GroupDangerRow title={M.addTemplate} tone="ai" disabled={add.isPending || q.data.templates.length >= COVER_LETTER_LIMITS.templates} onClick={() => add.mutate()} buttonTestId="cover-template-add" /> : null}
        </GroupList>
      </GroupSection>

      <GroupSection title={M.sectionDefault} help={<Help testId="help-cover-print" text={M.printDefaultHelp} />} data-testid="cover-sec-default">
        <GroupList>
          <GroupSwitchRow label={M.printDefault} name={M.printDefault} checked={printOn} disabled={!admin || savePrint.isPending} onChange={(v) => savePrint.mutate(v)} testId="cover-print-default" rowTestId="row-cover-print-default" />
        </GroupList>
      </GroupSection>

      <GroupSection title={M.sectionWindow} help={<Help testId="help-cover-window-sec" text={M.windowHelp} />} data-testid="cover-sec-window">
        <GroupList>
          <GroupNavRow title={M.envelopeTitle} value={COVER_ENVELOPE_KINDS[q.data.envelope.kind].label} to="/settings/cover-letters/envelope" testId="cover-envelope-row" buttonTestId="cover-envelope-open" />
        </GroupList>
      </GroupSection>
      {node}
    </div>
  );
}

/* ============================================================================
 * 設定 → 送付状 → 窓付封筒（/settings/cover-letters/envelope）
 * ========================================================================== */

type EnvKey = "x" | "y" | "w" | "h" | "dx" | "dy";
type EnvForm = Record<EnvKey, number | null>;
const ENV_KEYS: EnvKey[] = ["x", "y", "w", "h", "dx", "dy"];
const envFormOf = (e: CoverEnvelope): EnvForm => ({ x: e.x, y: e.y, w: e.w, h: e.h, dx: e.dx, dy: e.dy });
/** 欄の値 → 設定（空の欄があれば null） */
const envOf = (v: EnvForm): CoverEnvelope | null => (ENV_KEYS.some((k) => v[k] == null) ? null : { kind: "naga3", x: v.x!, y: v.y!, w: v.w!, h: v.h!, dx: v.dx!, dy: v.dy! });
/** 欄の誤り：空の欄はその欄に、範囲の外は節の最後の欄（窓＝高さ・微調整＝左右）に */
function envErrors(v: EnvForm): Record<string, string> | null {
  const empty = ENV_KEYS.find((k) => v[k] == null);
  if (empty) return { [empty]: M.envelopeRequired };
  const e = coverEnvelopeError(envOf(v)!);
  if (!e) return null;
  return { [e === M.envelopeShiftRange ? "dx" : "h"]: e };
}
const halfMm = (v: number | null): number | null => (v == null ? null : Math.round(v / COVER_ENVELOPE_RANGE.step) * COVER_ENVELOPE_RANGE.step);
const num = (v: number): string => String(Math.round(v * 100) / 100);

/**
 * 封筒の図（SVG・mm の座標）：封筒（宛名を読む向き＝横長。左が底・右が口）・紙（三つ折りの上の段）・窓・宛名の枠と見本の宛名（実寸の比率）。
 * 寸法の字は画面の文字サイズ（--fs-15）にそろえる＝図の幅から 1mm が何 px かを測って字の mm を決める。収まらなければ宛名の枠と字を朱
 */
export function EnvelopeFigure({ env, layout }: { env: CoverEnvelope; layout: CoverAddressLayout }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, font: 15 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setBox({ w: el.clientWidth, font: parseFloat(getComputedStyle(el).fontSize) || 15 });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const K = COVER_ENVELOPE_KINDS[env.kind];
  const P = COVER_PAPER_IN_ENVELOPE;
  const L = 30, R = 8, T = 6, B = 18;
  const vbW = K.w + L + R;
  const vbH = K.h + T + B;
  const pxPerMm = box.w > 0 ? box.w / vbW : 2.4;
  const f = Math.max(4, box.font / pxPerMm);
  const out = !layout.fits;
  const fr = { x: layout.frame.x + P.x, y: layout.frame.y + P.y, w: layout.frame.w, h: layout.frame.h };
  const ym = env.y + env.h / 2;
  const xm = env.x + env.w / 2;
  const tick = (x1: number, y1: number, x2: number, y2: number) => <line className="clenv-dim" x1={x1} y1={y1} x2={x2} y2={y2} />;
  // 見本の宛名（実寸＝pt を mm に）：行ごとに 行の高さの中ほどに字の中心
  let cy = fr.y;
  const texts = layout.lines.map((l, i) => {
    const sz = (l.strong ? layout.companyPt : layout.sizePt) * 0.3528;
    const lh = sz * (l.strong ? 1.45 : 1.5);
    if (l.gap) cy += 1;
    const y = cy + lh / 2 + sz * 0.35;
    cy += lh;
    return (
      <text key={i} x={fr.x} y={y} fontSize={sz} fontWeight={l.strong ? 700 : 400}>
        {l.text}
      </text>
    );
  });
  return (
    <div ref={ref} className="clenv" data-testid="cover-env-figure">
      <svg viewBox={`${-L} ${-T} ${vbW} ${vbH}`} role="img" aria-label={M.envelopeFigure}>
        <title>{M.envelopeFigure}</title>
        <rect className="clenv-env" x={0} y={0} width={K.w} height={K.h} />
        <rect className="clenv-paper" x={P.x} y={P.y} width={P.w} height={P.h} data-testid="cover-env-paper" />
        <text className="clenv-lbl" x={P.x + 2} y={P.y + P.h + f + 0.5} fontSize={f}>{M.figPaper}</text>
        <rect className="clenv-win" x={env.x} y={env.y} width={env.w} height={env.h} data-testid="cover-env-window" data-x={env.x} data-y={env.y} data-w={env.w} data-h={env.h} />
        <rect className={"clenv-frame" + (out ? " is-out" : "")} x={fr.x} y={fr.y} width={fr.w} height={fr.h} data-testid="cover-env-frame" data-x={num(layout.frame.x)} data-y={num(layout.frame.y)} data-fits={layout.fits ? "1" : "0"} />
        <g className={"clenv-addr" + (out ? " is-out" : "")} aria-label={M.figAddress} data-testid="cover-env-address" data-size={layout.sizePt}>
          {texts}
        </g>
        {/* 寸法：左端から・上端から・幅・高さ、封筒の外形 */}
        {tick(0, ym, env.x, ym)}
        <text className="clenv-num" x={env.x / 2} y={ym - 1.2} fontSize={f} textAnchor="middle" data-testid="cover-env-dim-x">{num(env.x)}</text>
        {tick(xm, 0, xm, env.y)}
        <text className="clenv-num" x={xm + 1.5} y={env.y / 2 + f * 0.35} fontSize={f} data-testid="cover-env-dim-y">{num(env.y)}</text>
        <text className="clenv-num" x={xm} y={env.y + env.h + f + 0.5} fontSize={f} textAnchor="middle" data-testid="cover-env-dim-w">{num(env.w)}</text>
        <text className="clenv-num" x={env.x + env.w + 1.5} y={ym + f * 0.35} fontSize={f} data-testid="cover-env-dim-h">{num(env.h)}</text>
        <text className="clenv-lbl" x={K.w / 2} y={K.h + f + 2} fontSize={f} textAnchor="middle">{`${K.label} ${K.w}×${K.h}`}</text>
        <text className="clenv-lbl" x={-2} y={K.h / 2 + f * 0.35} fontSize={f} textAnchor="end">{M.figBottom}</text>
        <text className="clenv-lbl" x={K.w - 2} y={K.h / 2 + f * 0.35} fontSize={f} textAnchor="end">{M.figMouth}</text>
      </svg>
    </div>
  );
}

export function SettingsCoverEnvelopePage() {
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const ids = useId();
  const { toast, node } = useToast();
  const pdfTab = usePdfTab();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<CoverLetterSettings>("GET", "/cover-letters/settings") });
  const a = useAutoSave<EnvForm>({
    server: q.data ? envFormOf(q.data.envelope) : null,
    enabled: admin,
    send: async (v) => {
      const r = await api<CoverLetterSettings>("PUT", "/cover-letters/settings", { envelope: envOf(v) });
      qc.setQueryData(KEY, r);
      return envFormOf(r.envelope);
    },
    check: (v) => {
      const errors = envErrors(v);
      return errors ? { status: SETTINGS_MSG.autoSaveFailed, errors } : null;
    },
    hide: (v) => (envOf(v) ? { url: "/api/v1/cover-letters/settings", method: "PUT", body: { envelope: envOf(v) } } : null),
  });
  const back = useBackNav({ to: "/settings/cover-letters", label: M.settingsTitle }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(M.envelopeTitle);
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || !a.form) return <p className="hint">読み込んでいます…</p>;
  const f = a.form;
  // 図は打っている値で（形に合わなければ保存してある値で）
  const typed = envOf(f);
  const valid = typed && !coverEnvelopeError(typed) ? typed : null;
  const env = valid ?? q.data.envelope;
  const layout = coverAddressLayout(env, coverSampleRecipient());
  const row = (k: EnvKey, label: string) => (
    <GroupNumberRow
      key={k}
      id={`${ids}${k}`}
      label={label}
      kind="dec"
      unit="mm"
      showZero
      value={f[k]}
      disabled={!admin}
      error={a.errors[k]}
      onChange={(v) => a.set({ [k]: halfMm(v) } as Partial<EnvForm>)}
      inputTestId={`cover-env-${k}`}
    />
  );
  const sheetUrl = `/api/v1/cover-letters/alignment-sheet${valid ? `?envelope=${encodeURIComponent(JSON.stringify(valid))}` : ""}`;
  return (
    <div className="ppage estpage clenvpage" data-testid="cover-envelope" {...a.formProps}>
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
        {admin ? <AutoSaveStatus state={a.state} testId="cover-env-autosave" /> : null}
      </div>
      {back.dialog}
      {guard.dialog}
      <div className="ptitle-row">
        <h1 className="ptitle">{M.envelopeTitle}</h1>
        <Help text={M.envelopeHelp} testId="help-cover-envelope" />
      </div>
      <AutoSaveNote state={a.state} testId="cover-env-autosave-note" />
      {!admin ? <p className="est-note">{SETTINGS_MSG.memberNote}</p> : null}
      <EnvelopeFigure env={env} layout={layout} />
      {valid && !layout.fits ? (
        <p className="err" role="alert" data-testid="cover-env-notfit">
          {`⚠ ${M.envelopeNotFit}`}
        </p>
      ) : null}
      <GroupSection title={M.sectionEnvelopeWindow} data-testid="cover-sec-env-window">
        <GroupList>
          <GroupValueRow title={M.envelopeKind} value={`${COVER_ENVELOPE_KINDS[env.kind].label}（${COVER_ENVELOPE_KINDS[env.kind].w}×${COVER_ENVELOPE_KINDS[env.kind].h}mm）`} testId="cover-env-kind" />
          {row("x", M.envelopeX)}
          {row("y", M.envelopeY)}
          {row("w", M.envelopeW)}
          {row("h", M.envelopeH)}
        </GroupList>
      </GroupSection>
      <GroupSection title={M.sectionEnvelopeShift} help={<Help testId="help-cover-env-shift" text={M.envelopeShiftHelp} />} data-testid="cover-sec-env-shift">
        <GroupList>
          {row("dy", M.envelopeDY)}
          {row("dx", M.envelopeDX)}
        </GroupList>
      </GroupSection>
      <GroupSection data-testid="cover-sec-env-actions">
        <GroupList>
          <GroupNavRow title={M.alignmentSheet} onClick={() => void pdfTab.open(sheetUrl)} disabled={!!pdfTab.busy} buttonTestId="cover-alignment" />
          {admin ? (
            <GroupDangerRow
              title={M.envelopeReset}
              tone="ai"
              onClick={() => {
                a.set(envFormOf(COVER_ENVELOPE_DEFAULT));
                toast(M.envelopeResetDone);
              }}
              buttonTestId="cover-env-reset"
            />
          ) : null}
        </GroupList>
      </GroupSection>
      {node}
    </div>
  );
}

type TForm = Omit<CoverLetterTemplate, "id" | "isDefault" | "sortNo" | "updatedAt">;
const tFormOf = (t: CoverLetterTemplate): TForm => ({
  name: t.name,
  titleFax: t.titleFax,
  titleWindow: t.titleWindow,
  greetingFax: t.greetingFax,
  greetingWindow: t.greetingWindow,
  body: t.body,
  remarks: t.remarks,
  printAddress: t.printAddress,
  printRepresentative: t.printRepresentative,
  printStaff: t.printStaff,
  printStaffEmail: t.printStaffEmail,
});
const changed = (v: TForm, base: TForm): Partial<TForm> => Object.fromEntries(Object.entries(v).filter(([k, x]) => x !== base[k as keyof TForm])) as Partial<TForm>;

export function SettingsCoverLetterPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const nav = useNavigate();
  const { toast, node } = useToast();
  const ids = useId();
  const tKey = ["cover-letter-template", id];
  const q = useQuery({ queryKey: tKey, queryFn: () => api<CoverLetterTemplate>("GET", `/cover-letters/templates/${id}`), retry: false });
  const version = q.data?.updatedAt;
  /** 直した欄だけを版つきで送る。ぶつかったら（409）最新を読んで、直した欄だけ重ねて送り直す（#202） */
  const put = async (patch: Partial<TForm>): Promise<CoverLetterTemplate> => {
    const send = (v: string | undefined) => api<CoverLetterTemplate>("PUT", `/cover-letters/templates/${id}`, { ...patch, ...(v ? { version: v } : {}) });
    try {
      return await send(qc.getQueryData<CoverLetterTemplate>(tKey)?.updatedAt ?? version);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 409 && e.code === "COVER_TEMPLATE_STALE")) throw e;
      const latest = await api<CoverLetterTemplate>("GET", `/cover-letters/templates/${id}`);
      return send(latest.updatedAt);
    }
  };
  const a = useAutoSave<TForm>({
    server: q.data ? tFormOf(q.data) : null,
    enabled: admin,
    send: async (v, base) => {
      const t = await put(changed(v, base));
      qc.setQueryData(tKey, t);
      void qc.invalidateQueries({ queryKey: KEY });
      return tFormOf(t);
    },
    check: (v) => {
      const r = CoverLetterTemplateInput.safeParse(v);
      if (r.success) return null;
      const errors: Record<string, string> = {};
      for (const i of r.error.issues) errors[String(i.path[0] ?? "")] ??= i.message;
      return { status: SETTINGS_MSG.autoSaveFailed, errors };
    },
    hide: (v, base) => ({ url: `/api/v1/cover-letters/templates/${id}`, method: "PUT", body: changed(v, base) }),
  });
  const back = useBackNav({ to: "/settings/cover-letters", label: M.settingsTitle }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(M.templateTitle);
  const [mode, setMode] = useState<CoverMode>("fax");
  const [ask, setAsk] = useState(false);
  const setDefault = useMutation({
    mutationFn: () => api<CoverLetterSettings>("POST", `/cover-letters/templates/${id}/default`),
    onSuccess: (s) => {
      qc.setQueryData(KEY, s);
      void qc.invalidateQueries({ queryKey: tKey });
      toast(M.setDefaultDone.replace("{name}", q.data?.name ?? ""));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const del = useMutation({
    mutationFn: () => api<void>("DELETE", `/cover-letters/templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      nav("/settings/cover-letters", { state: { notice: M.deleted.replace("{name}", q.data?.name ?? "") } });
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  // 見本（保存したテンプレートで描く。保存のたび・型を切り替えたら描き直す）
  const [sample, setSample] = useState<PageImg[]>([]);
  const [sampleErr, setSampleErr] = useState<string | undefined>();
  /** 窓付封筒の見本で宛名を 9pt まで縮めても窓に入らない（見本の下に注意の1行） */
  const [sampleLong, setSampleLong] = useState(false);
  useEffect(() => {
    if (!version) return;
    let alive = true;
    void (async () => {
      try {
        let long = false;
        const blob = await fetchFile(`/api/v1/cover-letters/templates/${id}/sample?mode=${mode}&v=${encodeURIComponent(version)}`, { onHeaders: (h) => (long = h.get(COVER_ADDRESS_HEADER) === "long") });
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        if (alive) {
          setSample(got);
          setSampleLong(long);
          setSampleErr(undefined);
        }
      } catch (e) {
        if (alive) setSampleErr(messageOf(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, mode, version]);

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || !a.form) return <p className="hint">読み込んでいます…</p>;
  const f = a.form;
  const t = q.data;
  const greet = (k: "greetingFax" | "greetingWindow", labelId: string) => (
    <GroupValueRow
      title={M.greeting}
      titleId={labelId}
      className="grow-seg"
      value={
        <Segmented
          options={(["none", "brief", "formal"] as const).map((g) => ({ value: g, label: COVER_GREETING_LABEL[g], testId: `${k}-${g}` }))}
          value={f[k]}
          onChange={(g: CoverGreeting) => a.set({ [k]: g } as Partial<TForm>)}
          labelledBy={labelId}
          disabled={!admin}
          testId={k}
        />
      }
    />
  );
  const sw = (k: "printAddress" | "printRepresentative" | "printStaff" | "printStaffEmail", label: string) => (
    <GroupSwitchRow label={label} name={label} checked={f[k]} disabled={!admin} onChange={(v) => a.set({ [k]: v } as Partial<TForm>)} testId={`cover-${k}`} />
  );
  return (
    <div className="ppage estpage" data-testid="cover-template" {...a.formProps}>
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
        {admin ? <AutoSaveStatus state={a.state} testId="cover-template-autosave" /> : null}
      </div>
      {back.dialog}
      {guard.dialog}
      <div className="ptitle-row">
        <h1 className="ptitle">{f.name || M.templateTitle}</h1>
        {t.isDefault ? <span className="chip" data-testid="cover-template-default">{M.defaultMark}</span> : null}
      </div>
      <AutoSaveNote state={a.state} testId="cover-template-autosave-note" />
      {!admin ? <p className="est-note">{SETTINGS_MSG.memberNote}</p> : null}

      <GroupSection data-testid="cover-sec-name">
        <GroupList>
          <GroupInputRow id={`${ids}n`} label={M.templateName} wide error={a.errors.name}>
            <input id={`${ids}n`} className="giinp" value={f.name} maxLength={COVER_LETTER_LIMITS.name} disabled={!admin} onChange={(e) => a.set({ name: e.target.value })} data-testid="cover-template-name" />
          </GroupInputRow>
        </GroupList>
      </GroupSection>
      <GroupSection title={M.sectionFax} data-testid="cover-sec-fax">
        <GroupList>
          <GroupInputRow id={`${ids}tf`} label={M.title} wide error={a.errors.titleFax}>
            <input id={`${ids}tf`} className="giinp" value={f.titleFax} maxLength={COVER_LETTER_LIMITS.title} disabled={!admin} onChange={(e) => a.set({ titleFax: e.target.value })} data-testid="cover-title-fax" />
          </GroupInputRow>
          {greet("greetingFax", `${ids}gf`)}
        </GroupList>
      </GroupSection>
      <GroupSection title={M.sectionWindowType} help={<Help testId="help-cover-window-type" text={M.windowHelp} />} data-testid="cover-sec-window-type">
        <GroupList>
          <GroupInputRow id={`${ids}tw`} label={M.title} wide error={a.errors.titleWindow}>
            <input id={`${ids}tw`} className="giinp" value={f.titleWindow} maxLength={COVER_LETTER_LIMITS.title} disabled={!admin} onChange={(e) => a.set({ titleWindow: e.target.value })} data-testid="cover-title-window" />
          </GroupInputRow>
          {greet("greetingWindow", `${ids}gw`)}
        </GroupList>
      </GroupSection>
      <GroupSection title={M.sectionBody} help={<Help testId="help-cover-body" text={`${M.greetingHelp}　${M.fieldsHelp}`} />} data-testid="cover-sec-body">
        <GroupList>
          <GroupTextRow className="grow-cltext">
            <label className="cltlbl" htmlFor={`${ids}b`}>{M.body}</label>
            <textarea id={`${ids}b`} className="inp cltext" rows={5} value={f.body} maxLength={COVER_LETTER_LIMITS.body} disabled={!admin} onChange={(e) => a.set({ body: e.target.value })} data-testid="cover-template-body" />
          </GroupTextRow>
          <GroupTextRow className="grow-cltext">
            <label className="cltlbl" htmlFor={`${ids}r`}>{M.remarks}</label>
            <textarea id={`${ids}r`} className="inp cltext" rows={2} value={f.remarks} maxLength={COVER_LETTER_LIMITS.remarks} disabled={!admin} onChange={(e) => a.set({ remarks: e.target.value })} data-testid="cover-template-remarks" />
          </GroupTextRow>
        </GroupList>
        {a.errors.body || a.errors.remarks ? <ErrorText>{a.errors.body ?? a.errors.remarks}</ErrorText> : null}
      </GroupSection>
      {/* 印字項目（「連絡のお願い」は 2026-10-21 射場さんの決め＝★10 でやめた） */}
      <GroupSection title={M.sectionPrint} data-testid="cover-sec-print">
        <GroupList>
          {sw("printAddress", M.printAddress)}
          {sw("printRepresentative", M.printRepresentative)}
          {sw("printStaff", M.printStaff)}
          {sw("printStaffEmail", M.printStaffEmail)}
        </GroupList>
      </GroupSection>
      <GroupSection title={M.preview} data-testid="cover-sec-sample">
        <div className="clsample">
          <Segmented options={[{ value: "fax" as CoverMode, label: M.sectionFax, testId: "cover-sample-fax" }, { value: "window" as CoverMode, label: M.sectionWindowType, testId: "cover-sample-window" }]} value={mode} onChange={setMode} label={M.preview} testId="cover-sample-mode" />
          {sampleErr ? <ErrorText>{sampleErr}</ErrorText> : null}
          {mode === "window" && sampleLong ? (
            <p className="err" role="alert" data-testid="cover-sample-long">
              {`⚠ ${M.addressTooLong}`}
            </p>
          ) : null}
          {sample.map((p, i) => (
            <div className="paperpage" key={i} data-testid="cover-sample-sheet">
              <img src={p.url} alt={`${M.preview} ${i + 1}ページ目`} width={p.w} height={p.h} />
            </div>
          ))}
        </div>
      </GroupSection>
      {admin ? (
        <GroupSection data-testid="cover-sec-actions">
          <GroupList>
            {!t.isDefault ? <GroupDangerRow title={M.setDefault} tone="ai" disabled={setDefault.isPending} onClick={() => setDefault.mutate()} buttonTestId="cover-set-default" /> : null}
            {!t.isDefault ? <GroupDangerRow title={M.delete} onClick={() => setAsk(true)} buttonTestId="cover-delete" /> : null}
          </GroupList>
          {t.isDefault ? <p className="hint">{M.defaultCannotDelete}</p> : null}
        </GroupSection>
      ) : null}
      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAsk(false)}>
          <div className="dlg" role="dialog" aria-label={M.delete} onKeyDown={(e) => e.key === "Escape" && setAsk(false)} data-testid="cover-delete-dialog">
            <div className="dh">{M.delete}</div>
            <div className="db">
              <p>{M.deleteAsk.replace("{name}", t.name)}</p>
            </div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setAsk(false)}>取消</button>
              <button type="button" className="btn primary danger" autoFocus disabled={del.isPending} onClick={() => del.mutate()} data-testid="cover-delete-confirm">
                {M.delete}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {node}
    </div>
  );
}
